import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { services, waLink } from "@/config/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-primary/20 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors min-h-[48px]";

export default function BookingModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", service: services[0].title, date: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const payload = {
      name: form.name,
      phone: form.phone,
      service: form.service,
      date: form.date || null,
      message: form.message || null,
    };
    // Works in two environments: on HTML/PHP hosting the same-origin
    // api/appointment.php saves the request; on the Emergent preview the
    // FastAPI backend handles it. PHP is tried first.
    const saveViaPhp = async () => {
      const res = await fetch("api/appointment.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error("php endpoint failed");
    };
    try {
      try {
        await saveViaPhp();
      } catch {
        await axios.post(`${API}/appointments`, payload);
      }
      toast.success("Request received! We'll confirm your appointment shortly.");
      setForm({ name: "", phone: "", service: services[0].title, date: "", message: "" });
      onClose();
    } catch {
      toast.error("Could not send right now — please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Book an appointment"
          className="fixed inset-0 z-[90] bg-deep/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-cream w-full max-w-lg p-8 sm:p-10 max-h-[92vh] overflow-y-auto"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                <h2 className="font-display text-3xl text-ink">Book an Appointment</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Tell us what you need — we'll confirm on call or WhatsApp.
                </p>
              </div>
              <button
                data-testid="booking-close-button"
                onClick={onClose}
                aria-label="Close booking form"
                className="p-2 -m-2 text-ink hover:text-primary transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="bk-name" className="block text-xs uppercase tracking-widest font-semibold text-ink mb-2">
                  Your Name
                </label>
                <input
                  id="bk-name"
                  data-testid="booking-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="e.g. Priya Sharma"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="bk-phone" className="block text-xs uppercase tracking-widest font-semibold text-ink mb-2">
                  Phone Number
                </label>
                <input
                  id="bk-phone"
                  data-testid="booking-phone-input"
                  required
                  type="tel"
                  minLength={8}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Your mobile number"
                  className={inputCls}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="bk-service" className="block text-xs uppercase tracking-widest font-semibold text-ink mb-2">
                    Service
                  </label>
                  <select
                    id="bk-service"
                    data-testid="booking-service-select"
                    value={form.service}
                    onChange={set("service")}
                    className={inputCls}
                  >
                    {services.map((s) => (
                      <option key={s.title} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="bk-date" className="block text-xs uppercase tracking-widest font-semibold text-ink mb-2">
                    Preferred Date
                  </label>
                  <input
                    id="bk-date"
                    data-testid="booking-date-input"
                    type="date"
                    value={form.date}
                    onChange={set("date")}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="bk-msg" className="block text-xs uppercase tracking-widest font-semibold text-ink mb-2">
                  Notes (optional)
                </label>
                <textarea
                  id="bk-msg"
                  data-testid="booking-message-input"
                  rows={3}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Anything we should know?"
                  className={`${inputCls} resize-none`}
                />
              </div>

              <button
                data-testid="booking-submit-button"
                type="submit"
                disabled={sending}
                className="w-full bg-primary text-primary-foreground px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-deep transition-colors disabled:opacity-60 min-h-[48px]"
              >
                {sending ? "Sending…" : "Request Appointment"}
              </button>
              <a
                data-testid="booking-whatsapp-alt"
                href={waLink("Hi Lavniya! I'd like to book an appointment.")}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 border border-wa text-wa px-8 py-3.5 text-sm font-semibold uppercase tracking-widest hover:bg-wa hover:text-white transition-colors min-h-[48px]"
              >
                <MessageCircle size={16} /> Or book on WhatsApp
              </a>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
